/**
 * Watchlist persistence — server copy per user, so the board survives logout.
 *
 * The browser copy (localStorage 'lm-watchlist') is wiped by logout and by the
 * idle-timeout logout (both call localStorage.clear()), which is why an added
 * patient used to vanish after logging back in. The server copy, keyed by the
 * logged-in user, is now the source of truth; the browser copy is only a fast
 * first paint and a fallback while the backend route is not deployed.
 *
 * reconcile() is pure so its rules can be tested:
 *   1. Server has never been saved for this user  → seed it from the browser copy.
 *   2. Server copy exists                          → it wins. A patient that is only
 *      in the STALE browser copy was removed on another computer: it stays removed.
 *   3. Anything the user added or removed in this session BEFORE the server copy
 *      arrived is kept / kept out — the load must not undo what they just did.
 */
import axios from 'axios';
import { liveApi } from '../../config/config';

const idsOf = (list) => { const s = {}; (list || []).forEach(p => { s[p.deviceId] = true; }); return s; };

/**
 * @param server        { exists, items } from GET /api/live/watchlist
 * @param current       the watchlist in state right now
 * @param initialLocal  the browser copy as it was when the page opened
 * @param removedIds    deviceIds the user removed this session before the load finished
 * @returns { list, push } — the list to show, and whether the server needs updating
 */
export function reconcile(server, current, initialLocal, removedIds) {
  current = current || [];
  const removed = {};
  (removedIds || []).forEach(id => { removed[id] = true; });

  if (!server || !server.exists) {
    // First time for this user: whatever is on this browser becomes their saved board.
    return { list: current, push: current.length > 0 };
  }

  const onServer = idsOf(server.items);
  const wasLocal = idsOf(initialLocal);
  const byId = {};
  current.forEach(p => { byId[p.deviceId] = p; });

  let changed = false;
  const list = [];
  (server.items || []).forEach(s => {
    if (removed[s.deviceId]) { changed = true; return; }        // removed while loading
    // keep richer fields the page already resolved (history link, latest name)
    list.push(Object.assign({}, s, byId[s.deviceId] || {}, {
      ward: (byId[s.deviceId] && byId[s.deviceId].ward) || s.ward || '',
      bed: (byId[s.deviceId] && byId[s.deviceId].bed) || s.bed || '',
    }));
  });
  current.forEach(p => {
    if (onServer[p.deviceId] || wasLocal[p.deviceId]) return;   // stale browser copy → stays removed
    list.push(p);                                               // added this session
    changed = true;
  });
  return { list: list, push: changed };
}

const slim = (list) => (list || []).map(p => ({
  deviceId: p.deviceId, patientId: p.patientId || '', name: p.name || '', ward: p.ward || '', bed: p.bed || '',
}));

export function fetchWatchlist() {
  return axios.get(`${liveApi}api/live/watchlist`).then(res => ({
    exists: !!(res.data && res.data.exists),
    items: (res.data && Array.isArray(res.data.items)) ? res.data.items : [],
  }));
}

/**
 * Save. POST first; if the server answers 404 it is running the first backend
 * version, which registered this route as PUT only — use that. (Both verbs are
 * allowed through the proxy; this just keeps the frontend working against
 * whichever backend build is deployed.)
 */
export function writeWithFallback(url, body) {
  return axios.post(url, body).catch(err => {
    if (err && err.response && err.response.status === 404) return axios.put(url, body);
    throw err;
  });
}

export function pushWatchlist(list) {
  return writeWithFallback(`${liveApi}api/live/watchlist`, { items: slim(list) });
}

/** Signature used to skip saving a list identical to the one last saved. */
export function watchlistSignature(list) { return JSON.stringify(slim(list)); }
